import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRole, hasRole } from "@/context/RoleContext";
import { daysRemaining, fmtDate } from "@/components/grc/shared";
import { SEVERITY_BADGE } from "@/components/grc/FindingsRegister";
import { TaskDetailDrawer } from "@/components/grc/TaskDetailDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle } from "lucide-react";

const COLUMNS = [
  { key: "Todo", label: "To Do", accent: "border-t-slate-400" },
  { key: "InProgress", label: "In Progress", accent: "border-t-blue-500" },
  { key: "Blocked", label: "Blocked", accent: "border-t-red-500" },
  { key: "Done", label: "Done", accent: "border-t-green-500" },
];

const initials = (name) =>
  (name || "?").split(/[@\s.]/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");

export const RemediationKanban = ({ assessmentId }) => {
  const role = useRole();
  const canMutate = hasRole(role, "compliance_officer");
  const [columns, setColumns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchBoard = useCallback(async () => {
    try {
      const { data } = await api.get(`/grc/${assessmentId}/remediation`);
      setColumns(data.columns);
    } catch {
      setColumns({ Todo: [], InProgress: [], Blocked: [], Done: [] });
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    setLoading(true);
    fetchBoard();
  }, [fetchBoard]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const prev = columns;
    const task = prev[source.droppableId].find((t) => t.task_id === draggableId);
    const next = { ...prev };
    next[source.droppableId] = prev[source.droppableId].filter((t) => t.task_id !== draggableId);
    next[destination.droppableId] = [...prev[destination.droppableId]];
    next[destination.droppableId].splice(destination.index, 0, { ...task, status: destination.droppableId });
    setColumns(next);
    try {
      await api.put(`/grc/remediation-tasks/${draggableId}`, { status: destination.droppableId });
      toast.success(`Task moved to ${destination.droppableId}`);
    } catch (e) {
      setColumns(prev);
      toast.error(e?.response?.data?.detail || "Failed to move task");
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
      </div>
    );
  }

  const total = COLUMNS.reduce((s, c) => s + (columns?.[c.key]?.length || 0), 0);

  return (
    <div className="space-y-3" data-testid="remediation-kanban">
      {total === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center text-sm text-slate-500" data-testid="kanban-empty-state">
          No remediation tasks yet. Tasks are auto-created for Critical and Major findings on import.
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {COLUMNS.map((col) => (
              <div key={col.key} className={`bg-slate-100/70 rounded-xl border-t-4 ${col.accent}`} data-testid={`kanban-column-${col.key}`}>
                <div className="px-3 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#003366]">{col.label}</span>
                  <span className="text-[10px] font-mono bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-500" data-testid={`kanban-count-${col.key}`}>
                    {columns?.[col.key]?.length || 0}
                  </span>
                </div>
                <Droppable droppableId={col.key} isDropDisabled={!canMutate}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`px-2 pb-2 space-y-2 min-h-[120px] rounded-b-xl transition-colors ${snapshot.isDraggingOver ? "bg-blue-50" : ""}`}
                    >
                      {(columns?.[col.key] || []).map((task, idx) => {
                        const days = daysRemaining(task.due_date);
                        const overdue = days !== null && days < 0 && task.status !== "Done";
                        return (
                          <Draggable key={task.task_id} draggableId={task.task_id} index={idx} isDragDisabled={!canMutate}>
                            {(p, snap) => (
                              <div
                                ref={p.innerRef}
                                {...p.draggableProps}
                                {...p.dragHandleProps}
                                onClick={() => setSelected(task)}
                                className={`bg-white rounded-lg border p-3 space-y-1.5 cursor-pointer hover:shadow-md transition-shadow ${
                                  snap.isDragging ? "shadow-lg ring-2 ring-[#008080]/40" : "border-slate-200"
                                } ${overdue ? "border-red-300" : ""}`}
                                data-testid={`kanban-card-${task.task_id}`}
                              >
                                <div className="flex items-center justify-between">
                                  {task.severity && (
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${SEVERITY_BADGE[task.severity] || "bg-slate-200 text-slate-700"}`}>
                                      {task.severity}
                                    </span>
                                  )}
                                  {overdue && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-red-600" data-testid={`kanban-overdue-${task.task_id}`}>
                                      <AlertTriangle size={11} /> {Math.abs(days)}d overdue
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-800 leading-snug line-clamp-2">
                                  {task.finding_title || task.description || "Untitled task"}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] text-slate-400">{fmtDate(task.due_date)}</span>
                                  {task.assignee_id && (
                                    <span
                                      className="w-5 h-5 rounded-full bg-[#003366] text-white text-[8px] font-bold flex items-center justify-center"
                                      title={task.assignee_id}
                                    >
                                      {initials(task.assignee_id)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      <TaskDetailDrawer
        task={selected}
        open={!!selected}
        canMutate={canMutate}
        onClose={() => setSelected(null)}
        onChanged={() => fetchBoard()}
      />
    </div>
  );
};
